import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Spin, Alert, Progress, Statistic, Divider, Space, Tooltip, Button, Modal, List, message } from 'antd';
import {
    CrownOutlined,
    CalendarOutlined,
    ThunderboltOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    WarningOutlined,
    StarOutlined,
    RocketOutlined,
    LockOutlined,
    ExclamationCircleOutlined,
    GiftOutlined,
    StopOutlined,
    ExperimentOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import { useLocation } from 'react-router-dom';
import { useScopedNavigate } from '../MarketerView/useScopedNavigate';
import {
    useGetOrganisationSubscriptionQuery,
    useGetOrganisationEntitlementsQuery,
    useGetOrganisationCreditsBalanceQuery,
    useGetActiveAddonsQuery,
    useCancelSubscriptionMutation,
    useGetSubscriptionsQuery,
    useGetTrialStatusQuery,
    IEntitlementCheckResponse,
    ICreditBalanceResponse,
    IOrganisationAddon,
    IFreeTrialStatus,
} from '../../services/nestjs-base-api';
import UpgradeToProfessionalModal from './UpgradeToProfessionalModal';
import { isCharterPlan, isProfessionalTier, planTierOf } from '../../utils/planTier';

const { Title, Text, Paragraph } = Typography;

// Friendly feature name mapping
const FEATURE_LABELS: Record<string, string> = {
    'UNRESTRICTED_ELEMENT_ACCESS': 'Full BE Model Access',
    'BENCHMARKING_OVERALL_ACCESS': 'Overall Benchmarking',
    'BENCHMARKING_PRODUCT_TYPE_ACCESS': 'Product Type Benchmarking',
    'BENCHMARKING_ALL_LEVELS_ACCESS': 'All Benchmarking Levels',
    'AI_EXPERTS_ACCESS': 'AI Experts',
    'AI_EXPERT_CREDITS_MONTHLY': 'AI Expert Credits',
    'AI_CREDITS_MONTHLY': 'Monthly AI Credits',
    'CUSTOMER_SEGMENTATION_FIELDS': 'Customer Segmentation',
};

// Which plan unlocks each feature (for locked features display)
const FEATURE_UNLOCK_PLAN: Record<string, string> = {
    'UNRESTRICTED_ELEMENT_ACCESS': 'Professional',
    'BENCHMARKING_PRODUCT_TYPE_ACCESS': 'Professional',
    'BENCHMARKING_ALL_LEVELS_ACCESS': 'Enterprise',
    'AI_EXPERTS_ACCESS': 'Professional',
    'AI_EXPERT_CREDITS_MONTHLY': 'Professional',
    'AI_CREDITS_MONTHLY': 'Professional',
    'CUSTOMER_SEGMENTATION_FIELDS': 'Professional',
};

// Features to hide (not implemented yet)
const HIDDEN_FEATURES = [
    'CUSTOMER_SEGMENTATION_FIELDS',
];

interface SubscriptionOverviewProps {
    organisationId: string;
    onUpgrade?: () => void; // Optional - custom handler for upgrade action
    disableUpgradeModal?: boolean; // When true, don't show internal upgrade modal (parent handles it)
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
    active: { color: 'green', icon: <CheckCircleOutlined /> },
    trialing: { color: 'blue', icon: <ClockCircleOutlined /> },
    past_due: { color: 'orange', icon: <WarningOutlined /> },
    canceled: { color: 'red', icon: <WarningOutlined /> },
    paused: { color: 'default', icon: <ClockCircleOutlined /> },
    founding_cohort: { color: 'gold', icon: <StarOutlined /> },
};

const FreeTrialCard: React.FC<{ trialStatus: IFreeTrialStatus; onUpgrade: () => void }> = ({ trialStatus, onUpgrade }) => {
    const isResponsePhase = trialStatus.phase === 'response_collection';
    const { collected, required, met, daysRemainingForResponses } = trialStatus.responseRequirement;
    const percent = Math.min(100, Math.round((collected / required) * 100));

    return (
        <Card
            style={{
                background: isResponsePhase
                    ? 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)'
                    : 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                border: `1px solid ${isResponsePhase ? '#91d5ff' : '#b7eb8f'}`,
                marginBottom: 24,
            }}
        >
            <Row gutter={[24, 16]} align="middle">
                <Col flex="auto">
                    <Space direction="vertical" size={4}>
                        <Space>
                            <ExperimentOutlined style={{ fontSize: 24, color: isResponsePhase ? '#1890ff' : '#52c41a' }} />
                            <Title level={4} style={{ margin: 0 }}>
                                <FormattedMessage
                                    id="freeTrial.card.title"
                                    defaultMessage="Professional Trial"
                                />
                            </Title>
                        </Space>
                        {isResponsePhase ? (
                            <>
                                <Text>
                                    <FormattedMessage
                                        id="freeTrial.card.responsePhase"
                                        defaultMessage="Collect {required} survey responses within {days} days to unlock your full {totalDays}-day trial."
                                        values={{ required, days: daysRemainingForResponses, totalDays: trialStatus.totalDays }}
                                    />
                                </Text>
                                <Progress
                                    percent={percent}
                                    status={met ? 'success' : 'active'}
                                    format={() => `${collected}/${required} responses`}
                                    style={{ maxWidth: 400 }}
                                />
                            </>
                        ) : (
                            <Text>
                                <FormattedMessage
                                    id="freeTrial.card.activeTrial"
                                    defaultMessage="Your trial is active! {days} days remaining. Subscribe to keep all Professional features."
                                    values={{ days: trialStatus.daysRemaining }}
                                />
                            </Text>
                        )}
                    </Space>
                </Col>
                <Col>
                    <Space direction="vertical" align="center" size={4}>
                        <Statistic
                            title={<FormattedMessage id="freeTrial.card.daysRemaining" defaultMessage="Days Remaining" />}
                            value={trialStatus.daysRemaining}
                            suffix={`/ ${trialStatus.totalDays}`}
                        />
                        {!isResponsePhase && (
                            <Button type="primary" icon={<RocketOutlined />} onClick={onUpgrade}>
                                <FormattedMessage id="freeTrial.card.subscribe" defaultMessage="Subscribe Now" />
                            </Button>
                        )}
                    </Space>
                </Col>
            </Row>
        </Card>
    );
};

const FoundingMemberCard: React.FC = () => {
    return (
        <Card
            style={{
                background: 'linear-gradient(135deg, #fffbe6 0%, #fff1b8 100%)',
                border: '2px solid #faad14',
                borderRadius: 16,
            }}
        >
            <Row align="middle" gutter={[24, 16]}>
                <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                    <StarOutlined style={{ fontSize: 48, color: '#faad14' }} />
                </Col>
                <Col xs={24} md={20}>
                    <Title level={4} style={{ margin: 0, marginBottom: 8, color: '#ad6800' }}>
                        <FormattedMessage id="subscription.founding.title" defaultMessage="Thank You, Founding Member!" />
                    </Title>
                    <Paragraph style={{ margin: 0, color: '#614700' }}>
                        <FormattedMessage
                            id="subscription.founding.description"
                            defaultMessage="As a founding member, you have unlimited access to all features including AI Experts, advanced benchmarking, and premium analytics. Your support helped build BrandScience.ai!"
                        />
                    </Paragraph>
                </Col>
            </Row>
        </Card>
    );
};

interface ProfessionalMemberCardProps {
    renewalDate?: string;
    cancelAtPeriodEnd?: boolean;
    billingCycle?: string;
    // Charter (SYR-106) gets the same card because it has the same
    // entitlements. Only the label differs — the cohort is a billing state and
    // a name, never a different set of permissions.
    isCharter?: boolean;
}

const ProfessionalMemberCard: React.FC<ProfessionalMemberCardProps> = ({ renewalDate, cancelAtPeriodEnd, billingCycle, isCharter }) => {
    const daysUntilRenewal = renewalDate
        ? Math.ceil((new Date(renewalDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        : null;

    return (
        <Card
            style={{
                background: cancelAtPeriodEnd
                    ? 'linear-gradient(135deg, #fff2e8 0%, #ffd8bf 100%)'
                    : 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
                border: cancelAtPeriodEnd ? '2px solid #fa8c16' : '2px solid #1890ff',
                borderRadius: 16,
            }}
        >
            <Row align="middle" gutter={[24, 16]}>
                <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                    <CrownOutlined style={{ fontSize: 48, color: cancelAtPeriodEnd ? '#fa8c16' : '#1890ff' }} />
                </Col>
                <Col xs={24} md={16}>
                    <Title level={4} style={{ margin: 0, marginBottom: 8, color: cancelAtPeriodEnd ? '#ad4e00' : '#0050b3' }}>
                        {cancelAtPeriodEnd ? (
                            <FormattedMessage id="subscription.professional.canceling" defaultMessage="Professional Plan - Canceling" />
                        ) : isCharter ? (
                            <FormattedMessage id="subscription.charter.title" defaultMessage="Charter Member" />
                        ) : (
                            <FormattedMessage id="subscription.professional.title" defaultMessage="Professional Plan Active" />
                        )}
                    </Title>
                    {cancelAtPeriodEnd ? (
                        <Alert
                            type="warning"
                            showIcon
                            message={
                                <FormattedMessage
                                    id="subscription.cancelAtPeriodEnd.message"
                                    defaultMessage="Your subscription will end on {date}. You'll retain access until then."
                                    values={{
                                        date: renewalDate
                                            ? new Date(renewalDate).toLocaleDateString('en-US', {
                                                  month: 'long',
                                                  day: 'numeric',
                                                  year: 'numeric',
                                              })
                                            : 'the end of your billing period',
                                    }}
                                />
                            }
                            style={{ marginBottom: 0 }}
                        />
                    ) : (
                        <Paragraph style={{ margin: 0, color: '#003a8c' }}>
                            <FormattedMessage
                                id="subscription.professional.description"
                                defaultMessage="You have full access to all Professional features including advanced analytics, product type benchmarking, and {credits} AI Expert credits per {period}."
                                values={{
                                    credits: billingCycle === 'annual' ? 240 : 20,
                                    period: billingCycle === 'annual' ? 'year' : 'month',
                                }}
                            />
                        </Paragraph>
                    )}
                </Col>
                {daysUntilRenewal !== null && (
                    <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                        <div style={{
                            background: 'rgba(255,255,255,0.7)',
                            borderRadius: 12,
                            padding: '12px 16px',
                        }}>
                            <Text style={{ display: 'block', color: cancelAtPeriodEnd ? '#ad4e00' : '#0050b3', fontSize: 12 }}>
                                <CalendarOutlined style={{ marginRight: 4 }} />
                                {cancelAtPeriodEnd ? (
                                    <FormattedMessage id="subscription.endsIn" defaultMessage="Ends in" />
                                ) : (
                                    <FormattedMessage id="subscription.renewal" defaultMessage="Renewal" />
                                )}
                            </Text>
                            <Text strong style={{ fontSize: 20, color: cancelAtPeriodEnd ? '#ad4e00' : '#0050b3' }}>
                                {daysUntilRenewal}
                            </Text>
                            <Text style={{ display: 'block', color: cancelAtPeriodEnd ? '#ad4e00' : '#0050b3', fontSize: 12 }}>
                                <FormattedMessage id="subscription.days" defaultMessage="days" />
                            </Text>
                        </div>
                    </Col>
                )}
            </Row>
        </Card>
    );
};

interface EnterpriseMemberCardProps {
    renewalDate?: string;
    cancelAtPeriodEnd?: boolean;
}

const EnterpriseMemberCard: React.FC<EnterpriseMemberCardProps> = ({ renewalDate, cancelAtPeriodEnd }) => {
    const daysUntilRenewal = renewalDate
        ? Math.ceil((new Date(renewalDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        : null;

    return (
        <Card
            style={{
                background: cancelAtPeriodEnd
                    ? 'linear-gradient(135deg, #fff2e8 0%, #ffd8bf 100%)'
                    : 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                border: cancelAtPeriodEnd ? '2px solid #fa8c16' : '2px solid #9333ea',
                borderRadius: 16,
            }}
        >
            <Row align="middle" gutter={[24, 16]}>
                <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                    <CrownOutlined style={{ fontSize: 48, color: cancelAtPeriodEnd ? '#fa8c16' : '#9333ea' }} />
                </Col>
                <Col xs={24} md={16}>
                    <Title level={4} style={{ margin: 0, marginBottom: 8, color: cancelAtPeriodEnd ? '#ad4e00' : '#581c87' }}>
                        {cancelAtPeriodEnd ? (
                            <FormattedMessage id="subscription.enterprise.canceling" defaultMessage="Enterprise Plan - Canceling" />
                        ) : (
                            <FormattedMessage id="subscription.enterprise.title" defaultMessage="Enterprise Plan Active" />
                        )}
                    </Title>
                    {cancelAtPeriodEnd ? (
                        <Alert
                            type="warning"
                            showIcon
                            message={
                                <FormattedMessage
                                    id="subscription.cancelAtPeriodEnd.message"
                                    defaultMessage="Your subscription will end on {date}. You'll retain access until then."
                                    values={{
                                        date: renewalDate
                                            ? new Date(renewalDate).toLocaleDateString('en-US', {
                                                  month: 'long',
                                                  day: 'numeric',
                                                  year: 'numeric',
                                              })
                                            : 'the end of your billing period',
                                    }}
                                />
                            }
                            style={{ marginBottom: 0 }}
                        />
                    ) : (
                        <Paragraph style={{ margin: 0, color: '#6b21a8' }}>
                            <FormattedMessage
                                id="subscription.enterprise.description"
                                defaultMessage="You have full access to all Enterprise features including unlimited brands, all benchmarking levels, custom integrations, and dedicated support."
                            />
                        </Paragraph>
                    )}
                </Col>
                {daysUntilRenewal !== null && (
                    <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                        <div style={{
                            background: 'rgba(255,255,255,0.7)',
                            borderRadius: 12,
                            padding: '12px 16px',
                        }}>
                            <Text style={{ display: 'block', color: cancelAtPeriodEnd ? '#ad4e00' : '#581c87', fontSize: 12 }}>
                                <CalendarOutlined style={{ marginRight: 4 }} />
                                {cancelAtPeriodEnd ? (
                                    <FormattedMessage id="subscription.endsIn" defaultMessage="Ends in" />
                                ) : (
                                    <FormattedMessage id="subscription.renewal" defaultMessage="Renewal" />
                                )}
                            </Text>
                            <Text strong style={{ fontSize: 20, color: cancelAtPeriodEnd ? '#ad4e00' : '#581c87' }}>
                                {daysUntilRenewal}
                            </Text>
                            <Text style={{ display: 'block', color: cancelAtPeriodEnd ? '#ad4e00' : '#581c87', fontSize: 12 }}>
                                <FormattedMessage id="subscription.days" defaultMessage="days" />
                            </Text>
                        </div>
                    </Col>
                )}
            </Row>
        </Card>
    );
};

interface FreeMemberCardProps {
    onUpgradeClick: () => void;
}

const FreeMemberCard: React.FC<FreeMemberCardProps> = ({ onUpgradeClick }) => {
    return (
        <Card
            style={{
                background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                border: '2px solid #d9d9d9',
                borderRadius: 16,
            }}
        >
            <Row align="middle" gutter={[24, 16]}>
                <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                    <ThunderboltOutlined style={{ fontSize: 48, color: '#8c8c8c' }} />
                </Col>
                <Col xs={24} md={14}>
                    <Title level={4} style={{ margin: 0, marginBottom: 8, color: '#262626' }}>
                        <FormattedMessage id="subscription.free.title" defaultMessage="Free Plan" />
                    </Title>
                    <Paragraph style={{ margin: 0, color: '#595959' }}>
                        <FormattedMessage
                            id="subscription.free.description"
                            defaultMessage="You're on the Free plan with limited features. Upgrade to Professional to unlock advanced analytics, benchmarking, and AI Expert credits."
                        />
                    </Paragraph>
                </Col>
                <Col xs={24} md={6} style={{ textAlign: 'center' }}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<RocketOutlined />}
                        onClick={onUpgradeClick}
                        style={{
                            background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                            border: 'none',
                            fontWeight: 600,
                        }}
                    >
                        <FormattedMessage id="subscription.free.upgrade" defaultMessage="Upgrade" />
                    </Button>
                </Col>
            </Row>
        </Card>
    );
};

interface CancelSubscriptionModalProps {
    visible: boolean;
    onCancel: () => void;
    onConfirm: (immediately: boolean) => Promise<void>;
    periodEndDate: string;
    activeAddons: IOrganisationAddon[];
    isLoading: boolean;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
    visible,
    onCancel,
    onConfirm,
    periodEndDate,
    activeAddons,
    isLoading,
}) => {
    const [cancelType, setCancelType] = useState<'end_of_period' | 'immediately'>('end_of_period');

    const handleConfirm = () => {
        onConfirm(cancelType === 'immediately');
    };

    return (
        <Modal
            title={
                <Space>
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                    <span>Cancel Subscription</span>
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="back" onClick={onCancel}>
                    Keep Subscription
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    danger
                    loading={isLoading}
                    onClick={handleConfirm}
                >
                    {cancelType === 'immediately' ? 'Cancel Now' : 'Cancel Subscription'}
                </Button>,
            ]}
            width={560}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Alert
                    message="Are you sure you want to cancel?"
                    description="Canceling your subscription will remove access to premium features."
                    type="warning"
                    showIcon
                />

                <div>
                    <Text strong>When should the cancellation take effect?</Text>
                    <div style={{ marginTop: 12 }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card
                                size="small"
                                hoverable
                                style={{
                                    border: cancelType === 'end_of_period' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setCancelType('end_of_period')}
                            >
                                <Space>
                                    <input
                                        type="radio"
                                        checked={cancelType === 'end_of_period'}
                                        onChange={() => setCancelType('end_of_period')}
                                    />
                                    <div>
                                        <Text strong>At end of billing period</Text>
                                        <br />
                                        <Text type="secondary">
                                            You'll keep access until {periodEndDate}
                                        </Text>
                                    </div>
                                </Space>
                            </Card>
                            <Card
                                size="small"
                                hoverable
                                style={{
                                    border: cancelType === 'immediately' ? '2px solid #ff4d4f' : '1px solid #d9d9d9',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setCancelType('immediately')}
                            >
                                <Space>
                                    <input
                                        type="radio"
                                        checked={cancelType === 'immediately'}
                                        onChange={() => setCancelType('immediately')}
                                    />
                                    <div>
                                        <Text strong style={{ color: '#ff4d4f' }}>Cancel immediately</Text>
                                        <br />
                                        <Text type="secondary">
                                            Access will be revoked right away. No refunds.
                                        </Text>
                                    </div>
                                </Space>
                            </Card>
                        </Space>
                    </div>
                </div>

                {activeAddons.length > 0 && (
                    <>
                        <Divider style={{ margin: '8px 0' }} />
                        <Alert
                            message="You have active add-ons"
                            description={
                                <div>
                                    <Text>
                                        The following add-ons will be canceled when your subscription ends:
                                    </Text>
                                    <List
                                        size="small"
                                        dataSource={activeAddons}
                                        style={{ marginTop: 8 }}
                                        renderItem={(addon) => (
                                            <List.Item style={{ padding: '4px 0', border: 'none' }}>
                                                <Space>
                                                    <GiftOutlined style={{ color: '#722ed1' }} />
                                                    <Text>{addon.addon?.name || 'Add-on'}</Text>
                                                    {addon.expiresAt && (
                                                        <Text type="secondary">
                                                            (expires {new Date(addon.expiresAt).toLocaleDateString()})
                                                        </Text>
                                                    )}
                                                </Space>
                                            </List.Item>
                                        )}
                                    />
                                    <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                                        Unused credits or add-on benefits will not be refunded.
                                    </Text>
                                </div>
                            }
                            type="error"
                            showIcon
                            icon={<GiftOutlined />}
                        />
                    </>
                )}

                <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
                    <Text strong>What you'll lose:</Text>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                        <li>Access to all premium features</li>
                        <li>Brand equity tracking and analytics</li>
                        <li>AI Expert conversations</li>
                        {activeAddons.length > 0 && (
                            <li style={{ color: '#ff4d4f' }}>
                                {activeAddons.length} active add-on{activeAddons.length > 1 ? 's' : ''}
                            </li>
                        )}
                    </ul>
                </div>
            </Space>
        </Modal>
    );
};

interface CreditBalanceCardProps {
    credits: ICreditBalanceResponse;
    entitlements?: IEntitlementCheckResponse[];
    isFree: boolean;
    isFoundingMember: boolean;
    onUpgradeClick: () => void;
}

const CreditBalanceCard: React.FC<CreditBalanceCardProps> = ({ credits, entitlements, isFree, isFoundingMember, onUpgradeClick }) => {
    const navigate = useScopedNavigate();
    const expiringCredits = credits.expiringCredits?.filter(e => {
        const expiryDate = new Date(e.expiresAt);
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        return daysUntilExpiry <= 30;
    }) || [];

    // Get limit and usage from entitlements (entitlements include addon credits)
    const aiCreditsEntitlement = entitlements?.find(e => e.feature === 'AI_EXPERT_CREDITS_MONTHLY');
    const totalLimit = aiCreditsEntitlement?.limit || credits.totalCredits || 0;
    const used = aiCreditsEntitlement?.used || 0;
    const availableCredits = Math.max(0, totalLimit - used);

    const hasNoCredits = availableCredits === 0 && totalLimit > 0;

    // Show unlimited for Founding members
    if (isFoundingMember) {
        return (
            <Card
                title={
                    <Space>
                        <ThunderboltOutlined style={{ color: '#faad14' }} />
                        <span><FormattedMessage id="subscription.credits.title" defaultMessage="AI Expert Credits" /></span>
                    </Space>
                }
            >
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <Text strong style={{ fontSize: 32, color: '#faad14' }}>
                        <FormattedMessage id="subscription.credits.unlimited" defaultMessage="Unlimited" />
                    </Text>
                    <div style={{ marginTop: 8 }}>
                        <Text type="secondary">
                            <FormattedMessage id="subscription.credits.unlimitedDescription" defaultMessage="As a Founding member, you have unlimited AI Expert credits" />
                        </Text>
                    </div>
                </div>
            </Card>
        );
    }

    // Show upgrade CTA for all Free users (they can't buy credits)
    if (isFree) {
        return (
            <Card
                style={{
                    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                    border: '1px solid #fde68a',
                }}
            >
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                    }}>
                        <ThunderboltOutlined style={{ fontSize: 32, color: 'white' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
                        <FormattedMessage id="subscription.credits.unlock" defaultMessage="Unlock AI Expert Credits" />
                    </Title>
                    <Paragraph type="secondary" style={{ marginBottom: 20 }}>
                        <FormattedMessage
                            id="subscription.credits.unlockDescription"
                            defaultMessage="Get AI-powered brand insights with Professional. Includes 20 credits per month (240 annually)."
                        />
                    </Paragraph>
                    <Button
                        type="primary"
                        size="large"
                        icon={<RocketOutlined />}
                        onClick={onUpgradeClick}
                        style={{
                            background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                            border: 'none',
                            fontWeight: 600,
                        }}
                    >
                        <FormattedMessage id="subscription.credits.upgradeCta" defaultMessage="Upgrade to Professional" />
                    </Button>
                </div>
            </Card>
        );
    }

    return (
        <Card
            title={
                <Space>
                    <ThunderboltOutlined style={{ color: '#F59E0B' }} />
                    <span><FormattedMessage id="subscription.credits.title" defaultMessage="AI Expert Credits" /></span>
                </Space>
            }
            extra={
                <Button
                    type="link"
                    onClick={() => navigate('/dashboard/organisation?tab=billing&subtab=addons')}
                >
                    <FormattedMessage id="subscription.credits.buyMore" defaultMessage="Buy More" />
                </Button>
            }
        >
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Statistic
                        title={<FormattedMessage id="subscription.credits.available" defaultMessage="Available" />}
                        value={availableCredits.toLocaleString()}
                        valueStyle={{ color: '#F59E0B', fontSize: 28 }}
                    />
                </Col>
                <Col span={12}>
                    <div>
                        <Text type="secondary"><FormattedMessage id="subscription.credits.breakdown" defaultMessage="Breakdown" /></Text>
                        <div style={{ marginTop: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text><FormattedMessage id="subscription.credits.planIncluded" defaultMessage="Plan included" /></Text>
                                <Text strong>{credits.planIncluded.toLocaleString()}</Text>
                            </div>
                            {credits.bonus > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text><FormattedMessage id="subscription.credits.bonus" defaultMessage="Bonus" /></Text>
                                    <Text strong style={{ color: '#52c41a' }}>{credits.bonus.toLocaleString()}</Text>
                                </div>
                            )}
                            {/* Show addon credits from entitlements (totalLimit - planIncluded) */}
                            {totalLimit > credits.planIncluded && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text><FormattedMessage id="subscription.credits.purchased" defaultMessage="Purchased" /></Text>
                                    <Text strong>{(totalLimit - credits.planIncluded).toLocaleString()}</Text>
                                </div>
                            )}
                            {credits.reward > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text><FormattedMessage id="subscription.credits.rewards" defaultMessage="Rewards" /></Text>
                                    <Text strong style={{ color: '#faad14' }}>{credits.reward.toLocaleString()}</Text>
                                </div>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
            {expiringCredits.length > 0 && (
                <>
                    <Divider style={{ margin: '16px 0' }} />
                    <Alert
                        type="warning"
                        showIcon
                        message={
                            <FormattedMessage
                                id="subscription.credits.expiring"
                                defaultMessage="{count} credits expiring soon"
                                values={{ count: expiringCredits.reduce((sum, e) => sum + e.amount, 0) }}
                            />
                        }
                    />
                </>
            )}
        </Card>
    );
};

// Get friendly label for feature code
const getFeatureLabel = (code: string): string => {
    return FEATURE_LABELS[code] || code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

const UsageMeter: React.FC<{ entitlement: IEntitlementCheckResponse }> = ({ entitlement }) => {
    const intl = useIntl();
    if (entitlement.type !== 'limit' || entitlement.limit === undefined) {
        return null;
    }

    const used = entitlement.used || 0;
    const limit = entitlement.limit;
    const percent = entitlement.percentUsed || (limit > 0 ? (used / limit) * 100 : 0);
    const remaining = entitlement.remaining || (limit - used);

    const getProgressStatus = () => {
        if (percent >= 100) return 'exception';
        if (percent >= 80) return 'normal';
        return 'success';
    };

    const getProgressColor = () => {
        if (percent >= 100) return '#ff4d4f';
        if (percent >= 80) return '#faad14';
        return '#52c41a';
    };

    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text>{getFeatureLabel(entitlement.feature)}</Text>
                <Text type="secondary">
                    {used.toLocaleString()} / {limit.toLocaleString()}
                </Text>
            </div>
            <Tooltip title={intl.formatMessage({ id: 'subscription.usage.remaining', defaultMessage: '{remaining} remaining' }, { remaining: remaining.toLocaleString() })}>
                <Progress
                    percent={Math.min(percent, 100)}
                    status={getProgressStatus()}
                    strokeColor={getProgressColor()}
                    showInfo={false}
                    size="small"
                />
            </Tooltip>
            {entitlement.resetsAt && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <FormattedMessage
                        id="subscription.usage.resetsAt"
                        defaultMessage="Resets {date}"
                        values={{ date: new Date(entitlement.resetsAt).toLocaleDateString() }}
                    />
                </Text>
            )}
        </div>
    );
};

const EntitlementsCard: React.FC<{ entitlements: IEntitlementCheckResponse[]; isFree?: boolean }> = ({ entitlements, isFree }) => {
    // Filter out hidden features
    const visibleEntitlements = entitlements.filter(e => !HIDDEN_FEATURES.includes(e.feature));
    // Hide usage limits for Free users (they don't have meaningful limits)
    const limitEntitlements = isFree ? [] : visibleEntitlements.filter(e => e.type === 'limit' && e.limit !== undefined);
    const booleanEntitlements = visibleEntitlements.filter(e => e.type === 'boolean');

    // Don't render if no visible entitlements
    if (limitEntitlements.length === 0 && booleanEntitlements.length === 0) {
        return null;
    }

    return (
        <Card
            title="Plan Features"
        >
            {limitEntitlements.length > 0 && (
                <div style={{ marginBottom: booleanEntitlements.length > 0 ? 24 : 0 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Usage Limits</Text>
                    {limitEntitlements.map(e => (
                        <UsageMeter key={e.feature} entitlement={e} />
                    ))}
                </div>
            )}

            {booleanEntitlements.length > 0 && (
                <div>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Feature Access</Text>
                    <Row gutter={[8, 8]}>
                        {booleanEntitlements.map(e => {
                            const unlockPlan = FEATURE_UNLOCK_PLAN[e.feature];
                            return (
                                <Col key={e.feature} span={24}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '8px 12px',
                                        background: e.hasAccess ? '#f6ffed' : '#fafafa',
                                        border: `1px solid ${e.hasAccess ? '#b7eb8f' : '#d9d9d9'}`,
                                        borderRadius: 6,
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {e.hasAccess ? (
                                                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                            ) : (
                                                <LockOutlined style={{ color: '#8c8c8c', marginRight: 8 }} />
                                            )}
                                            <Text style={{ color: e.hasAccess ? '#262626' : '#8c8c8c' }}>
                                                {getFeatureLabel(e.feature)}
                                            </Text>
                                        </div>
                                        {!e.hasAccess && unlockPlan && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {unlockPlan}
                                            </Text>
                                        )}
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            )}
        </Card>
    );
};

export const SubscriptionOverview: React.FC<SubscriptionOverviewProps> = ({ organisationId, onUpgrade, disableUpgradeModal }) => {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useScopedNavigate();
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);

    // Handler for upgrade clicks - uses parent handler if modal is disabled, otherwise shows internal modal
    const handleUpgradeClick = () => {
        if (disableUpgradeModal && onUpgrade) {
            onUpgrade();
        } else {
            setShowUpgradeModal(true);
        }
    };

    const { data: subscription, isLoading: subLoading, error: subError, refetch: refetchSubscription } = useGetOrganisationSubscriptionQuery({
        jwtToken: user?.jwtToken || '',
        orgId: organisationId,
    }, {
        skip: !user?.jwtToken || !organisationId,
    });

    const { data: entitlements, isLoading: entLoading } = useGetOrganisationEntitlementsQuery({
        jwtToken: user?.jwtToken || '',
        orgId: organisationId,
    }, {
        skip: !user?.jwtToken || !organisationId,
    });

    const { data: credits, isLoading: credLoading } = useGetOrganisationCreditsBalanceQuery({
        jwtToken: user?.jwtToken || '',
        orgId: organisationId,
    }, {
        skip: !user?.jwtToken || !organisationId,
    });

    const { data: trialStatus } = useGetTrialStatusQuery({
        jwtToken: user?.jwtToken || '',
        orgId: organisationId,
    }, {
        skip: !user?.jwtToken || !organisationId,
    });

    // Fetch active add-ons for cancel warning
    const { data: activeAddons = [] } = useGetActiveAddonsQuery(
        {
            jwtToken: user?.jwtToken || '',
            orgId: parseInt(organisationId, 10),
        },
        { skip: !user?.jwtToken || !organisationId }
    );

    // Fetch Stripe subscriptions to get subscription ID for cancellation
    const { data: stripeSubscriptions = [] } = useGetSubscriptionsQuery(
        {
            jwtToken: user?.jwtToken || '',
            organisationId: parseInt(organisationId, 10),
        },
        { skip: !user?.jwtToken || !organisationId }
    );

    const [cancelSubscription, { isLoading: cancelLoading }] = useCancelSubscriptionMutation();

    // Get active Stripe subscription (for cancellation)
    const activeStripeSubscription = stripeSubscriptions.find(
        (sub) => !['canceled', 'unpaid'].includes(sub.status)
    );

    const handleCancelSubscription = async (immediately: boolean) => {
        if (!activeStripeSubscription) {
            message.error('No active subscription found to cancel');
            return;
        }
        try {
            await cancelSubscription({
                jwtToken: user!.jwtToken,
                organisationId: parseInt(organisationId, 10),
                subscriptionId: activeStripeSubscription.id,
                immediately,
            }).unwrap();

            message.success(
                immediately
                    ? 'Subscription canceled immediately'
                    : 'Subscription will cancel at the end of the billing period'
            );
            setShowCancelModal(false);
            // Refetch subscription to update UI
            refetchSubscription();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to cancel subscription');
        }
    };

    // Check if we should show upgrade modal from URL hash
    // Skip if parent component handles the modal (disableUpgradeModal=true)
    useEffect(() => {
        if (!disableUpgradeModal && location.hash === '#upgrade') {
            setShowUpgradeModal(true);
            // Clean up the hash from URL
            navigate(location.pathname + location.search, { replace: true });
        }
    }, [disableUpgradeModal, location.hash, location.pathname, location.search, navigate]);

    if (subLoading || entLoading || credLoading) {
        return (
            <div style={{ textAlign: 'center', padding: 48 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (subError) {
        return (
            <Alert
                message={<FormattedMessage id="subscription.error.title" defaultMessage="Error Loading Subscription" />}
                description={<FormattedMessage id="subscription.error.description" defaultMessage="Unable to load subscription information. Please try again later." />}
                type="error"
                showIcon
            />
        );
    }

    // Check if subscription is in an active state (not canceled)
    const activeStatuses = ['active', 'trialing', 'past_due', 'founding_cohort'];
    const isSubscriptionActive = subscription ? activeStatuses.includes(subscription.status) : false;

    const isFoundingMember = subscription?.status === 'founding_cohort' || subscription?.plan?.code === 'FOUNDING';
    // Only consider paid plans if subscription is active.
    // Resolved through planTierOf rather than startsWith: CHARTER_ANNUAL is a
    // Professional-tier plan whose code does not begin with "PROFESSIONAL", and
    // a startsWith check drops it into isFree — showing an upgrade prompt to a
    // brand that has already paid for the year.
    const isCharter = isCharterPlan(subscription?.plan?.code);
    const isProfessional = isSubscriptionActive && isProfessionalTier(subscription?.plan?.code);
    const isEnterprise = isSubscriptionActive && planTierOf(subscription?.plan?.code) === 'ENTERPRISE';
    const isOnFreeTrial = trialStatus?.isOnFreeTrial === true;
    const isPaidPlan = isProfessional || isEnterprise || isFoundingMember;
    // Treat no subscription as Free plan
    const isFree = !subscription || !isPaidPlan;

    return (
        <div>
            {/* Free trial card */}
            {isOnFreeTrial && trialStatus && (
                <Row gutter={[24, 24]}>
                    <Col span={24}>
                        <FreeTrialCard trialStatus={trialStatus} onUpgrade={handleUpgradeClick} />
                    </Col>
                </Row>
            )}

            {/* Special cards for premium members */}
            {isFoundingMember && (
                <Row gutter={[24, 24]}>
                    <Col span={24}>
                        <FoundingMemberCard />
                    </Col>
                </Row>
            )}

            {isProfessional && !isFoundingMember && subscription && (
                <Row gutter={[24, 24]}>
                    <Col span={24}>
                        <ProfessionalMemberCard
                            renewalDate={subscription.currentPeriodEnd}
                            cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
                            billingCycle={subscription.billingCycle}
                            isCharter={isCharter}
                        />
                    </Col>
                </Row>
            )}

            {isEnterprise && !isFoundingMember && subscription && (
                <Row gutter={[24, 24]}>
                    <Col span={24}>
                        <EnterpriseMemberCard
                            renewalDate={subscription.currentPeriodEnd}
                            cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
                        />
                    </Col>
                </Row>
            )}

            {/* Free plan - show upgrade prompt */}
            {isFree && (
                <Row gutter={[24, 24]}>
                    <Col span={24}>
                        <FreeMemberCard onUpgradeClick={handleUpgradeClick} />
                    </Col>
                </Row>
            )}

            {/* Credits and Entitlements */}
            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                {credits && (
                    <Col xs={24} lg={12}>
                        <CreditBalanceCard
                            credits={credits}
                            entitlements={entitlements}
                            isFree={isFree}
                            isFoundingMember={isFoundingMember}
                            onUpgradeClick={handleUpgradeClick}
                        />
                    </Col>
                )}
                {entitlements && entitlements.length > 0 && (
                    <Col xs={24} lg={12}>
                        <EntitlementsCard entitlements={entitlements} isFree={isFree} />
                    </Col>
                )}
            </Row>

            {/* Cancel Subscription option - only show for paid plans with active Stripe subscription */}
            {isPaidPlan && activeStripeSubscription && !subscription?.cancelAtPeriodEnd && (
                <div style={{ marginTop: 24, textAlign: 'center' }}>
                    <Button
                        type="link"
                        danger
                        icon={<StopOutlined />}
                        onClick={() => setShowCancelModal(true)}
                    >
                        Cancel Subscription
                    </Button>
                </div>
            )}

            {/* Upgrade Modal - only render if parent doesn't handle it */}
            {!disableUpgradeModal && (
                <UpgradeToProfessionalModal
                    visible={showUpgradeModal}
                    onClose={() => setShowUpgradeModal(false)}
                    onUpgrade={onUpgrade}
                />
            )}

            {/* Cancel Subscription Modal */}
            <CancelSubscriptionModal
                visible={showCancelModal}
                onCancel={() => setShowCancelModal(false)}
                onConfirm={handleCancelSubscription}
                periodEndDate={
                    subscription?.currentPeriodEnd
                        ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                          })
                        : 'the end of your billing period'
                }
                activeAddons={activeAddons}
                isLoading={cancelLoading}
            />
        </div>
    );
};

export default SubscriptionOverview;
